// =============================================================
// 1) src/pages/DevArchivePage.js  (DEV‑only Archive viewer)
//    - Lists RESOLVED dev notes
//    - "Reopen" puts them back on Home (sets resolved=false)
//    - Page self‑guards: only visible if isDev
// =============================================================
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  updateDoc,
  doc,
} from 'firebase/firestore';

export default function DevArchivePage() {
  const { isDev, loadingAuth } = useAuth();
  const [notes, setNotes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    if (!isDev) return;
    setLoading(true);
    setError('');
    try {
      const q = query(
        collection(db, 'devInbox'),
        where('resolved', '==', true),
        orderBy('createdAt', 'desc'), // If Firestore asks for an index, follow the console link once.
        limit(50)
      );
      const snap = await getDocs(q);
      setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setError(e?.message || 'Failed to load archive');
    } finally {
      setLoading(false);
    }
  }, [isDev]);

  React.useEffect(() => {
    if (!loadingAuth && isDev) load();
  }, [loadingAuth, isDev, load]);

  const reopen = async (id) => {
    try {
      await updateDoc(doc(db, 'devInbox', id), { resolved: false });
      // Remove from archive list locally
      setNotes(curr => curr.filter(n => n.id !== id));
    } catch (e) {
      alert(e?.message || 'Failed to reopen');
    }
  };

  if (loadingAuth) return null;
  if (!isDev) return <div className="p-6">Not authorised.</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Developer Archive</h2>
        <button onClick={load} className="text-blue-600 hover:underline">Refresh</button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-gray-600">No archived notes.</div>
      ) : (
        <ul className="grid grid-cols-1 gap-3">
          {notes.map(n => {
            const created = n.createdAt?.toDate ? n.createdAt.toDate() : null;
            return (
              <li key={n.id} className="border rounded p-4 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{n.subject || '(No subject)'}</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      From: {n.fromName || n.fromEmail || n.fromUid || 'Unknown'}
                      {created && ` • ${created.toLocaleString('en-GB')}`}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">Resolved</span>
                </div>
                {n.message && (
                  <div className="text-sm whitespace-pre-wrap mt-2">{n.message}</div>
                )}
                {n.meta && (
                  <pre className="bg-gray-50 text-xs text-gray-700 p-2 rounded mt-2 overflow-auto">{JSON.stringify(n.meta, null, 2)}</pre>
                )}
                <div className="mt-3">
                  <button
                    onClick={() => reopen(n.id)}
                    className="px-3 py-1 rounded text-white bg-amber-600 hover:bg-amber-700"
                  >
                    Reopen
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}